import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Divider,
  InputAdornment,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  LocalOffer as CouponIcon,
  Percent as PercentIcon,
  ArrowBack as ArrowBackIcon,
  AttachMoney as MoneyIcon,
  CardGiftcard as GiftIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';
import { cacheEndpoints } from '../../utils/apiCache';

const EditCoupon = () => {
  const navigate = useNavigate();
  const { couponId } = useParams();
  const { actions } = useApp();
  
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    expiryDate: '',
    status: true,
    visibleToCustomer: true,
    description: '',
    minimumOrderAmount: '',
    maximumDiscount: '',
    usageLimit: '',
    skuMapping: '',
    freeSku: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loadedAllowedStoreIds, setLoadedAllowedStoreIds] = useState(null);

  useEffect(() => {
    fetchCoupon();
    const loadStores = async () => {
      try {
        const storesData = await cacheEndpoints.stores.getAll();
        setStores(storesData || []);
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };
    loadStores();
  }, [couponId]);

  useEffect(() => {
    if (!loadedAllowedStoreIds || stores.length === 0) return;
    const matched = stores.filter((s) => loadedAllowedStoreIds.includes(s.id));
    setSelectedStores(matched);
  }, [loadedAllowedStoreIds, stores]);

  const fetchCoupon = async () => {
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const response = await fetch(`${appConfig.api.baseURL}/coupons/${couponId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        const coupon = data.coupon;
        
        // Debug log to check the loaded data
        console.log('Loaded coupon data:', {
          visibleToCustomer: coupon.visibleToCustomer,
          status: coupon.status,
          code: coupon.code
        });
        
        // Format the expiry date for datetime-local input
        const expiryDate = new Date(coupon.expiryDate);
        const formattedExpiryDate = expiryDate.toISOString().slice(0, 16);

        setFormData({
          code: coupon.code || '',
          discountType: coupon.discountType || 'percentage',
          discountValue: coupon.discountValue?.toString() || '',
          expiryDate: formattedExpiryDate,
          status: coupon.status !== false,
          visibleToCustomer: coupon.visibleToCustomer !== undefined ? coupon.visibleToCustomer : true,
          description: coupon.description || '',
          minimumOrderAmount: coupon.minimumOrderAmount?.toString() || '',
          maximumDiscount: coupon.maximumDiscount?.toString() || '',
          usageLimit: coupon.usageLimit?.toString() || '',
          skuMapping: coupon.skuMapping != null ? String(coupon.skuMapping) : '',
          freeSku: coupon.freeSku != null ? String(coupon.freeSku) : ''
        });
        setLoadedAllowedStoreIds(
          Array.isArray(coupon.allowedStoreIds) ? coupon.allowedStoreIds : []
        );
      } else {
        actions.showSnackbar(data.message || 'Failed to fetch coupon', 'error');
        navigate('/super-admin/coupons/list');
      }
    } catch (error) {
      console.error('Error fetching coupon:', error);
      actions.showSnackbar('Error fetching coupon', 'error');
      navigate('/super-admin/coupons/list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Coupon code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Coupon code must be at least 3 characters';
    } else if (!/^[A-Z0-9]+$/.test(formData.code)) {
      newErrors.code = 'Coupon code must contain only uppercase letters and numbers';
    }

    if (formData.discountType === 'free_sku') {
      if (!formData.freeSku.trim()) {
        newErrors.freeSku = 'Free SKU is required';
      }
    } else if (!formData.discountValue) {
      newErrors.discountValue = 'Discount value is required';
    } else {
      const value = parseFloat(formData.discountValue);
      if (isNaN(value) || value <= 0) {
        newErrors.discountValue = 'Discount value must be a positive number';
      } else if (formData.discountType === 'percentage' && value > 100) {
        newErrors.discountValue = 'Percentage cannot exceed 100%';
      }
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else {
      const expiryDate = new Date(formData.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate <= today) {
        newErrors.expiryDate = 'Expiry date must be in the future';
      }
    }

    if (formData.minimumOrderAmount && parseFloat(formData.minimumOrderAmount) < 0) {
      newErrors.minimumOrderAmount = 'Minimum order amount cannot be negative';
    }

    if (formData.maximumDiscount && parseFloat(formData.maximumDiscount) < 0) {
      newErrors.maximumDiscount = 'Maximum discount cannot be negative';
    }

    if (formData.usageLimit && parseInt(formData.usageLimit) <= 0) {
      newErrors.usageLimit = 'Usage limit must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      actions.showSnackbar('Please fix the errors in the form', 'error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const response = await fetch(`${appConfig.api.baseURL}/coupons/${couponId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          discountValue: formData.discountType === 'free_sku' ? null : parseFloat(formData.discountValue),
          freeSku: formData.discountType === 'free_sku' ? String(formData.freeSku).trim().toUpperCase() : null,
          minimumOrderAmount: formData.minimumOrderAmount ? parseFloat(formData.minimumOrderAmount) : null,
          maximumDiscount: formData.discountType === 'free_sku' ? null : (formData.maximumDiscount ? parseFloat(formData.maximumDiscount) : null),
          usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
          skuMapping: formData.skuMapping ? String(formData.skuMapping).trim() : '',
          allowedStoreIds: selectedStores.map((s) => s.id),
          updatedAt: new Date().toISOString()
        })
      });

      const data = await response.json();

      if (data.success) {
        actions.showSnackbar('Coupon updated successfully!', 'success');
        navigate('/super-admin/coupons/list');
      } else {
        actions.showSnackbar(data.message || 'Failed to update coupon', 'error');
      }
    } catch (error) {
      console.error('Error updating coupon:', error);
      actions.showSnackbar('Error updating coupon. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateDiscount = (orderAmount = 1000) => {
    const discount = formData.discountType === 'percentage' 
      ? (orderAmount * parseFloat(formData.discountValue || 0)) / 100
      : parseFloat(formData.discountValue || 0);
    
    const maxDiscount = formData.maximumDiscount ? parseFloat(formData.maximumDiscount) : null;
    
    if (maxDiscount && discount > maxDiscount) {
      return maxDiscount;
    }
    
    return discount;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/super-admin/coupons/list')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <CouponIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Edit Coupon
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Coupon Code */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Coupon Code"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                error={!!errors.code}
                helperText={errors.code || 'Enter a unique coupon code (uppercase letters and numbers only)'}
                placeholder="SAVE20"
                required
              />
            </Grid>

            {/* Discount Type */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.discountType}>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  value={formData.discountType}
                  onChange={(e) => handleInputChange('discountType', e.target.value)}
                  label="Discount Type"
                >
                  <MenuItem value="percentage">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PercentIcon sx={{ mr: 1 }} />
                      Percentage
                    </Box>
                  </MenuItem>
                  <MenuItem value="fixed">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MoneyIcon sx={{ mr: 1 }} />
                      Fixed Amount
                    </Box>
                  </MenuItem>
                  <MenuItem value="free_sku">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <GiftIcon sx={{ mr: 1 }} />
                      Free SKU
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Discount Value */}
            {formData.discountType !== 'free_sku' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`Discount Value ${formData.discountType === 'percentage' ? '(%)' : '(₹)'}`}
                type="number"
                value={formData.discountValue}
                onChange={(e) => handleInputChange('discountValue', e.target.value)}
                error={!!errors.discountValue}
                helperText={errors.discountValue || `Enter discount ${formData.discountType === 'percentage' ? 'percentage' : 'amount'}`}
                inputProps={{
                  min: 0,
                  max: formData.discountType === 'percentage' ? 100 : undefined,
                  step: formData.discountType === 'percentage' ? 1 : 0.01
                }}
                required
              />
            </Grid>
            )}

            {/* Free SKU */}
            {formData.discountType === 'free_sku' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Free SKU"
                value={formData.freeSku}
                onChange={(e) => handleInputChange('freeSku', e.target.value.toUpperCase())}
                error={!!errors.freeSku}
                helperText={errors.freeSku || 'Product variant SKU given free when coupon is applied'}
                placeholder="CCC002"
                required
              />
            </Grid>
            )}

            {/* Expiry Date */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="datetime-local"
                value={formData.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                error={!!errors.expiryDate}
                helperText={errors.expiryDate || 'Select when the coupon expires'}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>

            {/* Minimum Order Amount */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Minimum Order Amount (₹)"
                type="number"
                value={formData.minimumOrderAmount}
                onChange={(e) => handleInputChange('minimumOrderAmount', e.target.value)}
                error={!!errors.minimumOrderAmount}
                helperText={errors.minimumOrderAmount || 'Leave empty for no minimum'}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            {/* Maximum Discount */}
            {formData.discountType !== 'free_sku' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Maximum Discount (₹)"
                type="number"
                value={formData.maximumDiscount}
                onChange={(e) => handleInputChange('maximumDiscount', e.target.value)}
                error={!!errors.maximumDiscount}
                helperText={errors.maximumDiscount || 'Leave empty for no maximum'}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
            )}

            {/* Usage Limit */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Usage Limit"
                type="number"
                value={formData.usageLimit}
                onChange={(e) => handleInputChange('usageLimit', e.target.value)}
                error={!!errors.usageLimit}
                helperText={errors.usageLimit || 'Leave empty for unlimited usage'}
                inputProps={{
                  min: 1
                }}
              />
            </Grid>

            {/* SKU Mapping */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SKU Mapping"
                value={formData.skuMapping}
                onChange={(e) => handleInputChange('skuMapping', e.target.value)}
                helperText={
                  formData.discountType === 'free_sku'
                    ? 'Optional: cart must contain one of these SKUs to use the coupon'
                    : 'Comma-separated product SKUs this coupon applies to. Leave empty to apply to all products.'
                }
                placeholder="SKU1, SKU2, SKU3"
              />
            </Grid>

            {/* Allowed Stores */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={stores}
                value={selectedStores}
                onChange={(_, newValue) => setSelectedStores(newValue)}
                getOptionLabel={(option) =>
                  option.storeName
                    ? `${option.storeName}${option.branchId ? ` (${option.branchId})` : ''}`
                    : option.id || ''
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Allowed stores"
                    helperText="Leave empty to allow all stores"
                    placeholder="Select stores"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.storeName || option.id}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.checked)}
                        color="success"
                      />
                    }
                    label="Active Status"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                    {formData.status ? 'Coupon is active and functional' : 'Coupon is disabled'}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.visibleToCustomer}
                        onChange={(e) => handleInputChange('visibleToCustomer', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Visible to Customer"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                    {formData.visibleToCustomer ? 'Customers can see this coupon' : 'Hidden from customer view'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                helperText="Optional description for the coupon"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                  size="large"
                >
                  {isSubmitting ? 'Updating...' : 'Update Coupon'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/super-admin/coupons/list')}
                  size="large"
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default EditCoupon; 